// The one HTTP client. Relative URLs only: in dev Vite proxies /v1 to the API, in
// production the API serves this app from the same origin.
//
// The browser sends the httpOnly gp_take cookie. We also keep the token from
// POST /v1/takes in memory and send it as a Bearer header, which an app-store wrapper
// will rely on (apps handle cookies poorly). The API reads Bearer first and never falls
// back to the cookie, so the header is only sent when a token is actually held.
//
// Every take token is also kept in localStorage (lib/tokens.ts) and sent back as
// X-GP-Prior-Tokens on submit and result reads, so the API can show this person's earlier
// takes (event before/after, retakes) to them and to no one else.

import type { TeamReportData } from "../components/TeamReport";
import type { IntakeOptions } from "./intake";
import { forgetPersonSession, personSession, rememberPersonSession } from "./person";
import { forgetResultKeys } from "./resultKey";
import { forgetTokens, keepToken, keptTokenFor, keptTokens, priorTokensFor } from "./tokens";
import type {
  CampaignLink,
  OpenedPersonalLink,
  Choice,
  ConsentLookup,
  MyConnections,
  MyDashboard,
  RaterAnswerBody,
  RaterSession,
  RatersView,
  ResultView,
  ResultViewV4,
  SaveResponseResult,
  StartTakeInput,
  StartTakeResult,
  TakeView,
  WriteupView,
} from "./types";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** True for network failures and 5xx: worth a retry. */
export const isRetryable = (e: unknown): boolean => !(e instanceof ApiError) || e.status >= 500 || e.status === 0;

let token: string | null = null;

export function setToken(next: string | null): void {
  token = next && next.length > 0 ? next : null;
}
export function getToken(): string | null {
  return token;
}

export interface RequestOptions {
  body?: unknown;
  /** Overrides the in-memory take token for one call (a result link key). */
  bearer?: string | null;
  /** Send no Authorization header at all (staff admin calls use the session cookie). */
  noAuth?: boolean;
  headers?: Record<string, string>;
  /** Let the request finish after the page closes (a reader leaving their results). */
  keepalive?: boolean;
  /** Cancels the request (a newer one superseded it). Use with request(), not get(): get() shares requests. */
  signal?: AbortSignal;
}

/** How long the finish screen waits for submit before saying so (review of #86: a hung request left people at 95% with nothing to tap). */
export const SUBMIT_TIMEOUT_MS = 30_000;

/** An AbortSignal that fires after `ms` (a plain timer: works in every browser). */
function timeoutSignal(ms: number): AbortSignal {
  const c = new AbortController();
  setTimeout(() => c.abort(), ms);
  return c.signal;
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  const auth = opts.noAuth ? null : opts.bearer || token;
  if (auth) headers.Authorization = `Bearer ${auth}`;
  // A signed-in test-taker (the cookie covers this site; the header covers embeds and apps).
  const person = opts.noAuth ? null : personSession();
  if (person) headers["X-GP-Session"] = person;

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: "same-origin",
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      ...(opts.keepalive ? { keepalive: true } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch {
    if (opts.signal?.aborted) throw new ApiError(0, "aborted", "Cancelled.");
    throw new ApiError(0, "network", "We couldn't reach the server. Check your connection.");
  }

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
    throw new ApiError(res.status, err?.code ?? "http_error", err?.message ?? "Something went wrong.", err?.details);
  }
  return data as T;
}

// Identical GETs already in flight share one request (React re-runs effects in dev, and
// two screens may ask for the same thing at once).
const inFlightGets = new Map<string, Promise<unknown>>();
export function get<T>(path: string, opts: Omit<RequestOptions, "body"> = {}): Promise<T> {
  const key = `${opts.noAuth ? "-" : opts.bearer || token || ""} ${JSON.stringify(opts.headers ?? {})} ${path}`;
  const existing = inFlightGets.get(key);
  if (existing) return existing as Promise<T>;
  const p = request<T>("GET", path, opts).finally(() => inFlightGets.delete(key));
  inFlightGets.set(key, p);
  return p;
}

const enc = encodeURIComponent;

export interface AppConfig {
  emailEnabled: boolean;
  /** "About you" options for the start form (older APIs: absent). */
  intake?: IntakeOptions;
  /** A write-up model is configured, so new results get a personalized write-up. */
  writeupsEnabled?: boolean;
  /** The price the paywall shows; null (or absent) when there's no paywall. */
  paywall?: { priceCents: number; currency: string } | null;
  /** The texts-and-calls checkbox, word for word ({terms} and {privacy} are links). Absent on older APIs. */
  smsConsent?: { version: string; label: string; disclosure: string };
}

/** X-GP-Prior-Tokens: the other take tokens this browser holds. */
/** Every take token this browser keeps, so signing in claims the takes it took. */
function keptHeaders(): Record<string, string> {
  const prior = keptTokens().map((t) => t.token);
  return prior.length ? { "X-GP-Prior-Tokens": prior.slice(0, 10).join(",") } : {};
}

function priorHeaders(takeId: string): Record<string, string> {
  const prior = priorTokensFor(takeId);
  return prior.length > 0 ? { "X-GP-Prior-Tokens": prior.join(",") } : {};
}

/**
 * The Bearer for one take: a result key if given, else this browser's kept token for that
 * take, else the in-memory one. The kept token matters where the gp_take cookie is
 * blocked (inside a cross-site iframe) and when an older take is opened after a newer one.
 */
const bearerFor = (takeId: string, key?: string | null): string | null => key || keptTokenFor(takeId) || token;

export interface AccountSession {
  sessionToken: string;
  expiresAt: string;
}
/** A right password or emailed link: signed in, or (authenticator app on) on to the code step. */
export type SignInStep = { done: true } | { done: false; challenge: string };
interface SignInResult {
  codeRequired: boolean;
  sessionToken?: string;
  challenge?: string;
  expiresAt: string;
}
function stepOf(r: SignInResult): SignInStep {
  if (r.codeRequired) return { done: false, challenge: r.challenge! };
  rememberPersonSession(r.sessionToken!);
  return { done: true };
}
export type CodeInput = { code: string } | { backupCode: string };
export interface PersonPasskey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}
export interface PersonSecurity {
  hasPassword: boolean;
  passkeys: PersonPasskey[];
  totp: { on: boolean; backupCodesLeft: number };
}
export interface TakeAccount {
  hasAccount: boolean;
  signedIn: boolean;
  email: string;
}
export interface MyTeam {
  teamId: string;
  name: string;
  joinCode: string;
  leader: boolean;
  sharedTakeId: string | null;
  sharedCompletedAt: string | null;
  /** Which sharing wording they agreed to: 1 = team report only, 2 = full results to leaders. */
  consentVersion: number;
  /** Shared a profile under an earlier wording: leaders see only a summary until they agree to the current one. */
  needsConsent: boolean;
}
export interface WhoAmI {
  signedIn: boolean;
  email: string | null;
  firstName: string | null;
}

/** Which sign-in a reset link is for: a test-taker's profiles, or the staff admin. */
export type ResetKind = "taker" | "staff";

/** The file name in a Content-Disposition header: the UTF-8 one (filename*) if there is one. */
export function fileNameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (utf8) {
    try {
      return decodeURIComponent(utf8);
    } catch {
      // fall through to the plain one
    }
  }
  return disposition.match(/filename="([^"]*)"/i)?.[1] || null;
}

export const api = {
  /**
   * Forgot password: the API answers the same whatever the email (and emails afterwards), so
   * the answer says nothing about who has an account. No session or token goes with it.
   */
  forgotPassword: (email: string) => request<{ message: string }>("POST", "/v1/account/forgot-password", { body: { email }, noAuth: true }),
  /** Is this reset link still good, and for which sign-in (and so which password rule)? */
  checkPasswordReset: (token: string) =>
    request<{ kind: ResetKind; minPasswordLength: number }>("POST", "/v1/account/reset-password/check", { body: { token }, noAuth: true }),
  /** Sets the new password. Signs that sign-in out everywhere; the person signs in next. */
  resetPassword: (token: string, newPassword: string) =>
    request<{ kind: ResetKind }>("POST", "/v1/account/reset-password", { body: { token, newPassword }, noAuth: true }),
  /** Save my results: a password, from a take this device took. Signs in. */
  async createAccount(takeId: string, password: string): Promise<void> {
    const s = await request<AccountSession>("POST", "/v1/account", { body: { takeId, password }, bearer: bearerFor(takeId), headers: priorHeaders(takeId) });
    rememberPersonSession(s.sessionToken);
  },
  /** Email and password. Also claims the takes this device holds. With an authenticator app on, a code step follows. */
  async signIn(email: string, password: string): Promise<SignInStep> {
    return stepOf(
      await request<SignInResult>("POST", "/v1/account/sign-in", { body: { email, password }, noAuth: false, headers: keptHeaders() }),
    );
  },
  /** "Email me a sign-in link": the same answer whatever the email. */
  requestSignInLink: (email: string) => request<{ message: string }>("POST", "/v1/account/sign-in-link", { body: { email }, noAuth: true }),
  /** Uses an emailed link (once). */
  async useSignInLink(token: string): Promise<SignInStep> {
    return stepOf(await request<SignInResult>("POST", "/v1/account/sign-in-link/use", { body: { token }, noAuth: true }));
  },
  /** The code step: a code from the app, or a backup code. */
  async finishWithCode(challenge: string, input: CodeInput): Promise<void> {
    const s = await request<AccountSession>("POST", "/v1/account/sign-in/code", { body: { challenge, ...input }, noAuth: true });
    rememberPersonSession(s.sessionToken);
  },
  /** Passkey sign-in, after the device answered (lib/personPasskeys.ts). A touch-only key with the app on gets the code step. */
  async signInWithPasskeyResponse(response: unknown): Promise<SignInStep> {
    return stepOf(await request<SignInResult>("POST", "/v1/account/passkeys/sign-in", { body: { response }, noAuth: true, headers: keptHeaders() }));
  },
  passkeySignInOptions: () => request<Record<string, unknown>>("POST", "/v1/account/passkeys/sign-in-options", { noAuth: true }),
  /** With the authenticator app on, adding a passkey takes a current code. */
  passkeyAddOptions: (input?: CodeInput) => request<Record<string, unknown>>("POST", "/v1/account/passkeys/add-options", { body: input ?? {} }),
  addPasskeyResponse: (response: unknown, name: string) => request<PersonPasskey>("POST", "/v1/account/passkeys", { body: { response, name } }),
  removePasskey: (id: string) => request<void>("DELETE", `/v1/account/passkeys/${enc(id)}`),
  security: () => get<PersonSecurity>("/v1/account/security"),
  /** For someone signed in by emailed links so far. */
  choosePassword: (newPassword: string) => request<void>("POST", "/v1/account/password", { body: { newPassword } }),
  totpSetup: () => request<{ otpauthUrl: string; secret: string }>("POST", "/v1/account/totp/setup"),
  totpConfirm: (code: string) => request<{ backupCodes: string[] }>("POST", "/v1/account/totp/confirm", { body: { code } }),
  totpDisable: (input: CodeInput) => request<void>("POST", "/v1/account/totp/disable", { body: input }),
  newBackupCodes: (input: CodeInput) => request<{ backupCodes: string[] }>("POST", "/v1/account/totp/backup-codes", { body: input }),
  /**
   * Signs this browser out: the session, and the tests and result links it kept, so on a
   * shared phone or iPad the next person sees nothing of the last (security review, Oct 23).
   * The server clears its cookies too. Signing in again brings everything back.
   */
  async signOut(): Promise<void> {
    try {
      await request<void>("POST", "/v1/account/sign-out");
    } finally {
      forgetPersonSession();
      forgetTokens();
      forgetResultKeys();
      setToken(null);
    }
  },
  /** Signs every other device out; this one stays. */
  signOutOthers: () => request<{ signedOut: number }>("POST", "/v1/account/sign-out-others"),
  whoAmI: () => get<WhoAmI>("/v1/account"),
  teamInvite: (code: string) => get<{ name: string; consent: string; alreadyMember: boolean }>(`/v1/teams/join/${enc(code)}`),
  shareWithTeam: (code: string, takeId: string) =>
    request<{ team: { id: string; name: string } }>("POST", `/v1/teams/join/${enc(code)}/share`, { body: { takeId, consent: true } }),
  leaveTeam: (teamId: string) => request<void>("POST", `/v1/teams/${enc(teamId)}/leave`),
  leadTeam: (token: string) => request<{ team: { id: string; name: string } }>("POST", "/v1/teams/lead", { body: { token } }),
  teamReport: (teamId: string) => get<{ team: { id: string; name: string }; report: TeamReportData }>(`/v1/teams/${enc(teamId)}/report`),
  myTeams: () => get<{ consentText: string; teams: MyTeam[] }>("/v1/me/teams"),
  /** Agree to the current sharing wording for a team I'm already in (leaders may then open my full results). */
  shareFullResults: (teamId: string) => request<{ consentVersion: number }>("POST", `/v1/teams/${enc(teamId)}/consent`),
  myProfiles: () => get<MyDashboard>("/v1/me"),
  myConnections: () => get<MyConnections>("/v1/me/connections"),
  disconnectApp: (id: string) => request<{ revoked: true }>("POST", `/v1/me/connections/${enc(id)}/revoke`),
  // "Connect an app" page. The request handle only ever travels in these bodies. The session
  // header (and the staff cookie) go along so the API can tell who is signed in on this browser.
  consentLookup: (handle: string) => request<ConsentLookup>("POST", "/v1/oauth/consent/lookup", { body: { request: handle } }),
  consentApprove: (handle: string, account: "staff" | "person", csrf: string) =>
    request<{ redirectTo: string }>("POST", "/v1/oauth/consent/approve", { body: { request: handle, account, csrf } }),
  consentDeny: (handle: string, csrf: string) =>
    request<{ redirectTo: string }>("POST", "/v1/oauth/consent/deny", { body: { request: handle, csrf } }),
  takeAccount: (takeId: string, key?: string | null) => get<TakeAccount>(`/v1/takes/${enc(takeId)}/account`, { bearer: bearerFor(takeId, key) }),

  async startTake(input: StartTakeInput): Promise<StartTakeResult> {
    const result = await request<StartTakeResult>("POST", "/v1/takes", { body: input });
    setToken(result.token);
    keepToken(result.take.id, result.token);
    return result;
  },
  /** The take this browser can resume. Without a cookie (embedded), tries the newest kept token. */
  async currentTake(): Promise<TakeView> {
    try {
      return await get<TakeView>("/v1/takes/current");
    } catch (e) {
      const newest = keptTokens()[0];
      if (!(e instanceof ApiError && e.status === 404) || token || !newest) throw e;
      const take = await get<TakeView>("/v1/takes/current", { bearer: newest.token });
      setToken(newest.token);
      return take;
    }
  },
  /** Campaign or event link (the QR target). */
  getCampaign: (slug: string) => get<CampaignLink>(`/v1/go/${enc(slug)}`),
  /** A personal link's details, by its token (in the body: never in a URL). */
  openPersonalLink: (token: string) => request<OpenedPersonalLink>("POST", "/v1/personal-links/open", { body: { token } }),
  /** A code for a free test: locks it to the email, and answers the start link (/i#t=...). */
  redeemCode: (code: string, email: string, emailConfirm: string) =>
    request<{ url: string; email: string }>("POST", "/v1/redeem", { body: { code, email, emailConfirm }, noAuth: true }),
  getTake: (takeId: string) => get<TakeView>(`/v1/takes/${enc(takeId)}`, { bearer: bearerFor(takeId) }),
  /** Opens the Stripe checkout for a take whose payment is due: where to send the person. */
  checkout: (takeId: string) =>
    request<{ url: string | null; discount?: "applied" | "unavailable" | null; alreadyPaid?: boolean }>("POST", `/v1/takes/${enc(takeId)}/checkout`, { bearer: bearerFor(takeId) }),
  /** Back from checkout: the server asks Stripe about that session. */
  confirmCheckout: (takeId: string, sessionId: string) =>
    request<{ payment: "due" | "paid" }>("POST", `/v1/takes/${enc(takeId)}/checkout/confirm`, { body: { sessionId }, bearer: bearerFor(takeId) }),
  saveResponse: (takeId: string, itemCode: string, value: Choice) =>
    request<SaveResponseResult>("PUT", `/v1/takes/${enc(takeId)}/responses/${enc(itemCode)}`, {
      body: { value },
      bearer: bearerFor(takeId),
    }),
  /** Gives up after SUBMIT_TIMEOUT_MS (status 0, like no connection): the finish screen then offers Try again, which is safe (a repeat submit returns early). */
  submit: (takeId: string) =>
    request<ResultView | ResultViewV4>("POST", `/v1/takes/${enc(takeId)}/submit`, {
      bearer: bearerFor(takeId),
      headers: priorHeaders(takeId),
      signal: timeoutSignal(SUBMIT_TIMEOUT_MS),
    }),
  /** `key`: a result link key from the results email (opens this take's result and raters only). A scoring v4 take returns a ResultViewV4. */
  getResult: (takeId: string, key?: string | null) =>
    get<ResultView | ResultViewV4>(`/v1/takes/${enc(takeId)}/result`, { bearer: bearerFor(takeId, key), headers: priorHeaders(takeId) }),
  /** The results PDF as a file, for the Download PDF button. */
  /** The results PDF, and the name the server gave it (the take's event and date: pdf-name.ts). */
  async resultPdf(takeId: string, key?: string | null): Promise<{ blob: Blob; fileName: string | null }> {
    const headers: Record<string, string> = { Accept: "application/pdf", ...priorHeaders(takeId) };
    const auth = bearerFor(takeId, key);
    if (auth) headers.Authorization = `Bearer ${auth}`;
    let res: Response;
    try {
      res = await fetch(`/v1/takes/${enc(takeId)}/result.pdf`, { headers, credentials: "same-origin" });
    } catch {
      throw new ApiError(0, "network", "We couldn't reach the server. Check your connection.");
    }
    if (!res.ok) throw new ApiError(res.status, "http_error", "We couldn't make the PDF. Try again.");
    return { blob: await res.blob(), fileName: fileNameFrom(res.headers.get("content-disposition")) };
  },
  /** App switches the server decides (whether results emails go out). */
  getConfig: () => get<AppConfig>("/v1/config"),
  /** The write-up on its own, for asking again while it is being written. */
  getWriteup: (takeId: string, key?: string | null) =>
    get<WriteupView>(`/v1/takes/${enc(takeId)}/writeup`, { bearer: bearerFor(takeId, key) }),
  /** Thumbs up or down on the write-up. */
  /** How far down their results they've read (25, 50, 75, 100). Fire and forget. */
  sendScrollDepth: (takeId: string, scrollDepth: 25 | 50 | 75 | 100, key?: string | null) =>
    request<void>("POST", `/v1/takes/${enc(takeId)}/engagement`, { body: { scrollDepth }, bearer: bearerFor(takeId, key), keepalive: true }),
  rateWriteup: (takeId: string, vote: "up" | "down", key?: string | null) =>
    request<{ vote: "up" | "down" }>("POST", `/v1/takes/${enc(takeId)}/writeup/feedback`, {
      body: { vote },
      bearer: bearerFor(takeId, key),
    }),
  /** They tapped Text (Email on a computer), Share or Copy on one of their links: how the invite card is used. Never waits, never fails. */
  /** A start form was opened (reports: the funnel's first step). Fire and forget. */
  startViewed(body: { kind: "direct" | "event" | "rater" | "team" | "personal"; campaign?: string; ref?: string; touch?: Record<string, string> }): void {
    void request("POST", "/v1/start-views", { body, keepalive: true }).catch(() => undefined);
  },
  markShared(takeId: string, code: string, via: "text" | "share" | "copy", key?: string | null): void {
    void request("POST", `/v1/takes/${enc(takeId)}/raters/${enc(code)}/shared`, { body: { via }, bearer: bearerFor(takeId, key), keepalive: true }).catch(() => undefined);
  },
  getRaters: (takeId: string, key?: string | null) =>
    get<RatersView>(`/v1/takes/${enc(takeId)}/raters`, { bearer: bearerFor(takeId, key) }),

  // Rater (observer) side: public, by link code.
  getRaterSession: (code: string) => get<RaterSession>(`/v1/rate/${enc(code)}`),
  saveRaterAnswer: (code: string, itemCode: string, body: RaterAnswerBody) =>
    request<{ itemCode: string; answeredCount: number; totalCount: number }>(
      "PUT",
      `/v1/rate/${enc(code)}/answers/${enc(itemCode)}`,
      { body },
    ),
  submitRater: (code: string) => request<RaterSession>("POST", `/v1/rate/${enc(code)}/submit`),
  /** The rater funnel: their finish screen seen, or its button tapped. Never throws. */
  raterOfferEvent: (code: string, event: "seen" | "clicked") =>
    request<void>("POST", `/v1/rate/${enc(code)}/offer`, { body: { event }, keepalive: true }).catch(() => undefined),
  emailRaterOffer: (code: string, email: string, consent: boolean) =>
    request<{ message: string }>("POST", `/v1/rate/${enc(code)}/email-me`, { body: { email, consent } }),
};
